import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, delay, map } from 'rxjs/operators';
import { User, ApiUserResponse } from '../models/approval-flow-definition.model';
import { ApprovalService } from './approval.service';
import { environment } from '../../../../services/global-environment';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  // private apiUrl = `${environment.apiUrl}/api/users`;
  private apiUrl = environment.apiUrl;

  // 添加一個屬性來存儲當前用戶ID (模擬環境)
  private mockCurrentUserId: string = 'user5'; // 預設使用李品管 (品管部門主管)

  constructor(
    private http: HttpClient,
    private approvalService: ApprovalService
  ) { }

  getAllUsers(): Observable<User[]> {
    return this.getUsersFromAPI();
  }

  // 新增：從實際 API 獲取用戶資料
  getUsersFromAPI(): Observable<User[]> {

    console.log('getUsersFromAPI called');

    const companyid = environment.companyid;


    console.log('getUsersFromAPI: companyid =', companyid, 'apiUrl =', this.apiUrl);

    if (!companyid || !this.apiUrl) {
      console.warn('Company ID or API URL not found in environment, returning empty array');
      return of([]);
    }

    const url = `${this.apiUrl}/api/users/getusers?companyId=${companyid}`;
    console.log('Fetching users from:', url);

    return this.http.get<ApiUserResponse>(url).pipe(
      map(response => {
        console.log('API Response for users:', response);
        if (response.success && response.data) {
          // 轉換 API 回應格式為現有的 User 介面格式
          const transformedUsers = response.data.map(apiUser => ({
            id: apiUser.id,
            name: apiUser.username,
            departmentId: "", // API 回應中沒有部門資訊，設為 undefined
            roleId: '', // 使用 position 作為 roleId
            email: apiUser.email
          }));
          console.log('Transformed users:', transformedUsers);
          return transformedUsers;
        }
        console.warn('API response success=false or no data, returning empty array');
        return [];
      }),
      catchError(error => {
        console.error('Error fetching users from API, falling back to mock data:', error);
        return [];
      })
    );
  }

  // 添加獲取當前用戶ID的方法
  getCurrentUserId(): string {
    // 實際環境中，這裡應該從認證服務或 localStorage/sessionStorage 獲取已登入用戶的 ID
    return "";
  }

  // 可選：添加設置當前用戶ID的方法 (用於測試)
  setCurrentUserId(userId: string): void {
    this.mockCurrentUserId = userId;
  }

  // 可選：獲取當前用戶詳細信息
  getCurrentUserDetails(): Observable<User | null> {
    const userId = this.getCurrentUserId();
    if (!userId) return of(null);

    return this.http.get<User>(`${this.apiUrl}/api/users/${userId}`).pipe(
      catchError(error => {
        console.error('Error fetching current user details', error);
        return of(null);
      })
    );
  }

  // 新增：透過使用者 ID 取得使用者名稱
  getUsernameById(userId: string): Observable<string | null> {
    const companyid = environment.companyid;

    if (!companyid || !userId) {
      console.warn('Company ID or User ID not provided');
      return of(null);
    }

    const url = `${this.apiUrl}/api/users/get-username`;
    const params = {
      companyid: companyid,
      id: userId
    };

    console.log('Fetching username for user ID:', userId, 'from:', url);

    return this.http.get<{
      success: boolean;
      username?: string;
      error?: string;
      message?: string;
    }>(url, { params }).pipe(
      map(response => {
        console.log('API Response for username:', response);
        if (response.success && response.username) {
          return response.username;
        } else {
          console.warn('API 回應異常或找不到使用者:', response);
          return null;
        }
      }),
      catchError(error => {
        console.error('取得使用者名稱失敗:', error);
        return of(null);
      })
    );
  }

  /**
   * Get complete user information (full name, email, etc.)
   * @param userIds - Array of user IDs
   * @returns Promise<any[]> - Array of user information [{id, fullName, username, email}]
   */
  async getUsersInfo(userIds: string[]): Promise<any[]> {
    const companyId = environment.companyid;

    if (!companyId || !this.apiUrl || !userIds || userIds.length === 0) {
      return [];
    }

    try {
      const url = `${this.apiUrl}/api/users/get-users-by-ids`;
      const requestBody = {
        companyid: companyId,
        ids: userIds
      };

      const response: any = await this.http.post(url, requestBody).toPromise();
      
      if (response && response.success) {
        // API response has data field containing the array
        const usersArray = response.data || response.users || [];
        
        if (Array.isArray(usersArray) && usersArray.length > 0) {
          return usersArray;
        } else if (typeof usersArray === 'object') {
          // Convert object format to array if needed
          return Object.values(usersArray);
        } else {
          return [];
        }
      } else {
        return [];
      }
    } catch (error) {
      console.error('Failed to fetch users info:', error);
      return [];
    }
  }
}
